"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { useUserRole } from "@/providers/user-role-provider"
import { stripLocalePrefix, isForceDarkBooking, isGlassNavPath, isChromelessPath } from "@/i18n/routing"

// Top-right CTA for logged-out viewers — mirrors the position of
// DesktopNotificationsHeader's bell so the corner is "occupied" in
// both auth states. Self-hides once the user signs in (the bell takes
// over). Hidden on /auth/* and the marketing landing where the page
// already drives its own conversion CTAs.
// /invite/* is a shop-invite landing with its own "Create your account to
// join" CTA — a second corner "Sign Up" doubles up and confuses the invitee.
// /preview/* are design-QA surfaces that render their own complete chrome —
// a floating corner CTA on top of them defeats the point of the preview.
// /listings and /hiring (logged-out) render PublicNavGlass, which carries
// its own Join pill — same reasoning as isGlassNavPath below, called out
// separately here because both pages intentionally keep the rest of the app
// chrome (FloatingNav, bell) for signed-in visitors, so they aren't in that
// list. Miss one and the corner gets a Sign Up sitting beside the pill's
// Join, which is what happened when /hiring moved to the glass nav.
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients", "/dashboard/client", "/invite", "/preview", "/listings", "/hiring"]
// /changelog and /traction render their own dark headers with a Sign Up
// button, so this CTA would double up in the corner — hide it on those.

function shouldHide(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  // Compare locale-agnostically — the Spanish landing lives at /es and must
  // hide this CTA exactly like / does (PublicNavbar owns the corner there).
  const pathname = stripLocalePrefix(rawPathname)
  // Pages the glass nav owns carry the whole site in their menu panel — this
  // would be a second, competing one on the same screen.
  if (isChromelessPath(pathname)) return true
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`))
}

export function PublicAuthCta() {
  const t = useTranslations("pages.auth.cta")
  const pathname = usePathname()
  const { userId, isLoading } = useUserRole()

  if (isLoading) return null
  if (userId) return null
  if (shouldHide(pathname)) return null

  // Booking/discovery pages are `bg-background` surfaces that follow app dark
  // mode — there the black pill sank into the near-black page, so it inverts
  // to white. Marketing pages hardcode light content and keep the black pill
  // (a dark: variant would render white-on-white for dark-mode devices).
  const p = stripLocalePrefix(pathname || "")
  // The client booking storefront (/book/[slug] + /book/quick/[slug]) is force-
  // dark for EVERY visitor, so the pill must be the inverted (white) button
  // there regardless of device theme — applied directly below, not via a
  // `dark:` variant (this CTA sits outside the .force-dark wrapper, and html may
  // be light). Other token surfaces (/barbers, /listings, /book/shops) follow
  // the app theme and get the dark: inversion.
  const isDarkBooking = isForceDarkBooking(pathname)
  const onTokenSurface =
    !isDarkBooking &&
    ["/book", "/barbers", "/listings"].some((x) => p === x || p.startsWith(`${x}/`))

  // Single, stable "Sign Up" CTA (no cycling) — the clearest entry point for a
  // logged-out visitor. Sign In stays reachable from the signup page toggle.
  return (
    <div className="fixed safe-top-cta right-3 md:right-5 z-40">
      <Link
        href="/auth/signup"
        aria-label={t("signUp")}
        className={`inline-flex items-center justify-center h-7 md:h-9 px-3 md:px-4 rounded-full text-[11px] md:text-xs font-medium shadow-md hover:scale-[1.03] active:scale-95 transition-transform duration-150 ${
          isDarkBooking
            ? "bg-white text-black ring-1 ring-white/20"
            : `bg-black text-white ${onTokenSurface ? "dark:bg-white dark:text-black dark:ring-1 dark:ring-white/20" : ""}`
        }`}
      >
        {t("signUp")}
      </Link>
    </div>
  )
}
